import { HttpClient } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { firstValueFrom, Observable } from "rxjs"
import { environment } from "../../environments/environment"
import { Unit, UserService } from "./user.service"
import { AngularFireDatabase } from "@angular/fire/compat/database"
import { AuthService } from "./auth.service"

@Injectable({
    providedIn: "root",
})
export class UnitService {
    selectedUnit: Unit
    createUserApiUrl = `${environment.primeApiUrl}/createUser`
    constructor(
        private afDatabase: AngularFireDatabase,
        private userService: UserService,
        private http: HttpClient,
        private authService: AuthService
    ) {
        this.trackSelectedUnit();
    }

    /**
     * Remember which unit is selected, for the many callers that read
     * `selectedUnit.routeName`.
     *
     * This used to also open a live listener on `Clients/{route}/orderQueue`
     * and keep the result in a public `orderQueue` field that nothing ever
     * read — a realtime subscription to every order in the unit, held open for
     * every signed-in user from app start, feeding nobody. Removed with the
     * adminv2 KDS view.
     */
    trackSelectedUnit() {
        this.userService.selectedUnit2$.subscribe(x => {
            if (!x) return;
            this.selectedUnit = x;
        })
    }

    async getUserGroups(routeName) {
        let franchiseId = await this.getFranchiseId(routeName);
        return await this.http
            .get<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${franchiseId}`).toPromise();
    }
    async updateUserGroup(group) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/usergroup/${group.id}`, { Code: group.Code }).toPromise();
    }
    async getUserGroupCoupons(routeName) {
        let franchiseId = await this.getFranchiseId(routeName);
        return await this.http
            .get<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/coupons/${franchiseId}?routeName=${routeName}`).toPromise();
    }
    async getUsersForUserGroup(groupId) {
        return await this.http
            .get<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/users`).toPromise();
    }

    async getUsersForUserGroupPaginated(groupId: string, page: number, size: number, search: string) {
        return await this.http.get<any>(
            `${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/usersPaginated?page=${page}&size=${size}&search=${search}`
        ).toPromise();
    }

    async getAllUsers() {
        let routeName = this.selectedUnit.routeName;
        return await this.http
            .get<any>(`${environment.pubQueueApiUrlEU}/v1/units/users`, { params: { unit: routeName } }).toPromise();
    }
    async addUserToUserGroup(user, routeName, groupId) {
        let franchiseId = await this.getFranchiseId(routeName);
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}`, { user, franchiseId }).toPromise();
    }
    async linkCouponToGroup(id: any, couponId: any) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/usergroups/linkCoupon`, { usergoupId: id, couponId: couponId }).toPromise();
    }
    async createInvoiceImplementation(invoice: any, groupId) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/invoice/create`, { invoice: invoice }).toPromise();
    }
    async updateInvoiceImplementation(invoice: any, groupId) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/invoice/update`, { invoice: invoice }).toPromise();
    }
    async saveInvoiceForCoupon(previousInvoice: any, newInvoice: any, coupon: any) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrl}/api/v1/coupons/updateInvoiceIntegrations`, { previousInvoice, newInvoice, coupon }).toPromise();
    }
    async getDomainsForUserGroup(groupId) {
        return await this.http.get<string[]>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/domains`).toPromise();
    }
    async addDomainToUserGroup(groupId, domain) {
        let body = { domain };
        return await this.http.post(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/domains`, body).toPromise();
    }
    async removeDomainFromUserGroup(groupId, domain) {
        let body = { domain };
        return await this.http.post(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/domains/delete`, body).toPromise();
    }
    async deleteUserFromUserGroup(groupId, userId) {
        return await this.http.delete<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/users/${userId}`).toPromise();
    }
    async getCouponsForUserGroup(groupId, routeName) {
        return await this.http
            .get<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/coupons?routeName=${routeName}`).toPromise();
    }
    async getInvoiceIntegrationsForUserGroup(groupId) {
        return await this.http
            .get<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/invoices`).toPromise();
    }
    async createCouponForUserGroup(groupId, coupon) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/coupons`, { coupon }).toPromise();
    }
    async deleteCouponFromUserGroup(groupId, couponId) {
        return await this.http.delete<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${groupId}/coupons/${couponId}`).toPromise();
    }

    async searchUser(routeName, email) {
        let authType = email.startsWith("+") ? "phone" : "email"
        let foundUsers = [];
        let body = {
            routeName: routeName,
            searchValue: email,
            authType
        }
        foundUsers = await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/users/users/userstoSearch`, body).toPromise();

        return foundUsers;

    }
    async updateUserGroupUser(user) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/user/update`, { user }).toPromise();
    }
    async createUserGroup(name, routeName) {
        let franchiseId = await this.getFranchiseId(routeName);
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups`, { franchiseId, userGroupName: name }).toPromise();
    }
    async deleteUserGroup(id) {
        return await this.http.delete<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${id}`).toPromise();

    }
    async deleteInvoiceFromUser(userId: any, invoiceId: any) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/user/removeInvoice`, { userId: userId, invoiceId: invoiceId }).toPromise();
    }
    async addInvoiceToUser(userId: any, invoiceId: any) {
        return await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/user/addInvoice`, { userId: userId, invoiceId: invoiceId }).toPromise();
    }
    async getFranchiseId(routeName) {
        return (await this.afDatabase.database.ref(`adminApp/clients/${routeName}/settings/receiptApi/voucherFranchiseId`).once("value")).val();
    }
    async setFranchiseId(routeName) {
        this.selectedUnit.franchiseId = await this.getFranchiseId(routeName);
        return this.selectedUnit.franchiseId;
    }
    getUsers(): Observable<any> {

        return this.http.post(`${environment.pubQueueApiUrlEU}/v1/users/retrieve`, {
            unitId: this.userService.selectedUnit2$.value.id,
            userId: this.userService.user.value.id
        });

    }

    async updateUser(user) {
        try {
            let unit = this.authService.currentSalesLocationValue;
            this.afDatabase.object(`usersAdminApp/${user.id}/info`).update({
                name: user.name,
                email: user.email,
            })
            let result = await this.http.post(`${environment.pubQueueApiUrlEU}/v1/units/users/update`, {
                userId: user.id,
                name: user.name,
                email: user.email,
                role: user.role,
                unitId: unit.salesLocationId,
            }).toPromise();

        } catch (error) {

        }

    }

    async updateBulkUser(user, units) {

        try {
            await Promise.all(
                units.map(unit =>
                    this.http.post(`${environment.pubQueueApiUrlEU}/v1/units/users/update`, {
                        userId: user.id,
                        name: user.name,
                        email: user.email,
                        role: unit.role,
                        unitId: unit.value.salesLocationId,
                    }
                    ).toPromise()
                )
            );
        } catch (error) {
            console.error(error);
        }
    }

    async removeUserFromBulkUnits(user, units) {
        try {
            this.afDatabase.database
                .ref(`adminApp/clients/${this.selectedUnit.routeName}/users/${user.key}`)
                .set(null);

            await Promise.all(
                units.map(unit => {
                    return this.http.post(
                        `${environment.pubQueueApiUrlEU}/v1/units/users/remove`,
                        {
                            userId: user.id,
                            unitId: unit.value.salesLocationId,
                            orgId: unit.value.organisationId,
                            companyId: unit.value.companyId
                        }
                    ).toPromise();
                })
            );

        } catch (error) {
            console.error(error);
        }
    }


    getUserName(userId: string): Observable<any> {
        return this.afDatabase.object(`usersAdminApp/${userId}/info`).valueChanges();
    }

    async removeUser(user) {
        let unit = this.authService.currentSalesLocationValue;
        this.afDatabase.database
            .ref(
                `adminApp/clients/${this.selectedUnit.routeName}/users/${user.key}`
            )
            .set(null);
        let result = await this.http.post(`${environment.pubQueueApiUrlEU}/v1/units/users/remove`, {
            userId: user.id,
            unitId: unit.salesLocationId,
            orgId: unit.organisationId,
            companyId: unit.companyId
        }).toPromise();

        return result
    }
    async getAppLocations(routeName: string) {
        let result = await this.http.get<any>(`${environment.pubQueueApiUrlEU}/v1/apps/unit/${routeName}`)
            .toPromise();
        return result;
    }
    async updateAppLocation(data) {
        let result = await this.http.post<any>(`${environment.pubQueueApiUrlEU}/v1/apps/unit`, data)
            .toPromise();
        return result;
    }
    async inviteUser(user, units?: any) {
        try {
            let unit = this.authService.currentSalesLocationValue;
            let userId = this.userService.user.value;
            let result = await this.http.post(`${environment.pubQueueApiUrlEU}/v1/units/users/invite`,
                {
                    unitRoute: unit.salesLocationRoute,
                    unitId: unit.salesLocationId,
                    unitName: unit.salesLocationName,
                    orgRoute: unit.organisationRoute,
                    orgId: unit.organisationId,
                    userId,
                    email: user.email,
                    name: user.name,
                    role: user.role,
                    companyId: unit.companyId,
                    units
                }).toPromise();
        } catch (error: any) {
            const message = error?.error?.message || 'Failed to invite user';
            throw new Error(message);
        }
    }

    async inviteUserToUnit(user, unit) {
        let userId = this.userService.user.value;
        let result = await this.http.post(`${environment.pubQueueApiUrlEU}/v1/units/users/invite`,
            {
                unitRoute: unit.value.salesLocationRoute,
                unitId: unit.value.salesLocationId,
                unitName: unit.value.salesLocationName,
                orgRoute: unit.value.organisationRoute,
                orgId: unit.value.organisationId,
                userId,
                email: user.email,
                name: user.name,
                role: user.role,
                companyId: unit.value.companyId
            }).toPromise();
    }
    async createUser(user) {
        var promise = new Promise((resolve, reject) => {
            this.http
                .post<any>(this.createUserApiUrl, {
                    user: {
                        name: user.name,
                        email: user.email,
                        password: user.password,
                    },
                })
                .subscribe((x) => {
                    this.afDatabase.database
                        .ref(
                            `adminApp/clients/${this.selectedUnit.routeName}/users/${x.data}`
                        )
                        .set(true)
                    this.afDatabase.database
                        .ref(`usersAdminApp/${x.data}/clientGroups/0`)
                        .once("value")
                        .then((snapshot) => {
                            let clientGroup = snapshot.val()
                            if (clientGroup && clientGroup.units) {
                                clientGroup.units.push(this.selectedUnit)
                                snapshot.ref
                                    .update(clientGroup)
                                    .then((done) => {
                                        resolve(x.data)
                                    })
                            } else {
                                let newGroup = {
                                    id: this.userService.userClientGroupId,
                                    units: [this.selectedUnit],
                                }
                                snapshot.ref.update(newGroup).then((done) => {
                                    resolve(x.data)
                                })
                            }
                        })
                })
        })
        return promise
    }
    async saveOpeningHours(hours, deviations, automatically, routeName) {
        await this.afDatabase.database
            .ref(
                `Clients/${routeName}/AppInfo/Context/openCloseAutomatically`
            )
            .set(automatically);
        return Promise.all([
            this.afDatabase.database
                .ref(
                    `adminApp/clients/${routeName}/settings/openingHours/weekdays`
                )
                .set(hours),
            this.afDatabase.database
                .ref(
                    `adminApp/clients/${routeName}/settings/openingHours/deviations`
                )
                .set(deviations)
        ])

    }
    getAboutUs(routeName) {
        return this.afDatabase
            .object<string>(`Clients/${routeName}/AppInfo/Context/aboutUsDescription`)
            .valueChanges();
    }
    saveAboutUs(routeName, text) {
        this.afDatabase
            .object<string>(`Clients/${routeName}/AppInfo/Context/aboutUsDescription`).set(text)
    }
    getOpeningHours(routeName) {
        return this.afDatabase
            .object(`adminApp/clients/${routeName}/settings/openingHours`)
            .valueChanges()
    }
    async getOpenCloseAutomatically(routeName) {
        return (await this.afDatabase.database.ref(`Clients/${routeName}/AppInfo/Context/openCloseAutomatically`).get()).val();
    }

    getAppDetails(routeName) {
        const path = `clientUnits/${routeName}`;
        return this.afDatabase
            .object(path)
            .valueChanges();
    }

    async updateAppChildrenOrder(parentRouteName: string, allIndex: number, orderedChildren: any[]): Promise<void> {
        try {
            const path = `clientUnits/${parentRouteName}/all/${allIndex}/children`;
            await this.afDatabase.database.ref(path).set(orderedChildren);
        } catch (error: any) {
            throw new Error(error);
        }

    }
    async updateKioskChildrenOrder(parentRouteName: string, orderedChildren: any[]): Promise<void> {
        try {
            const path = `clientUnits/${parentRouteName}/all`;
            await this.afDatabase.database.ref(path).set(orderedChildren);
        } catch (error: any) {
            throw new Error(error);
        }
    }
    async setToggleFlags(routeName) {
        toggleFlags = new Flags();
        let flags = (
            await this.afDatabase
                .object(`adminApp/clients/${routeName}/settings/flags`)
                .query.once("value")
        ).val()
        for (const key in flags) {
            if (Object.prototype.hasOwnProperty.call(flags, key)) {
                const element = flags[key]
                toggleFlags[key] = element
            }
        }
    }
    async createOffer(offer, routeName) {
        return this.http
            .post<any>(`${environment.pubQueueApiUrl}/api/v1/voucher/create`, {
                offer,
                userId: this.userService.user.value.id,
                routeName: routeName
            }).toPromise();

    }
    async deleteOffer(offer, routeName) {
        return this.http
            .post<any>(`${environment.pubQueueApiUrl}/api/v1/voucher/delete`, {
                offer,
                userId: this.userService.user.value.id,
                routeName: routeName
            }).toPromise();

    }

    async sendPushToTopic(pushData: { title: string, body: string, id?: string, imageUrl: string, groupIds?: any, mode: string }, routeName: string) {
        let projectId = (await this.afDatabase.database.ref(`adminApp/clients/${routeName}/settings/firebaseProjectId`).once("value")).val() || "default";

        const data: any = {
            title: pushData.title,
            body: pushData.body,
            projectId,
            routeName,
            imageUrl: pushData.imageUrl,
        };

        if (pushData.mode !== 'all' && pushData.groupIds?.length) {
            data.groupIds = pushData.groupIds;
        }
        const url = pushData.mode === 'all'
            ? `${environment.pubQueueApiUrlEU}/v1/unit/push`
            : `${environment.pubQueueApiUrlEU}/v1/unit/push/toGroup`;

        return await this.http.post<any>(url, data).toPromise();
    }


    async getPushNotificationsHistory(routeName: string) {
        let url = `${environment.pubQueueApiUrlEU}/v1/unit/push/latest?routeName=${routeName}`;
        return await this.http.post<any>(url, {}).toPromise();
    }

    async getFilteredPushNotificationsHistory(routeName: string, from: number, to: number) {
        let url = `${environment.pubQueueApiUrlEU}/v1/unit/push/filtered/?routeName=${routeName}&from=${from}&to=${to}`;
        return await this.http.get<any>(url).toPromise();
    }
    async updateInfo(unit: Unit) {
        return await this.afDatabase.database.ref(`adminApp/clients/${unit.routeName}/info/public`).update(unit.info);
    }
    async getInfo(unit: Unit) {
        return (await this.afDatabase.database.ref(`adminApp/clients/${unit.routeName}/info/public`).once("value")).val();
    }
    async updateUnit(data) {
        return await this.http
            .post<any>(
                `${environment.pubQueueApiUrlEU}/v1/organisation/units/update`,
                data
            )
            .toPromise()
    }

    getBusinessInfo(body: any): Promise<any> {

        const endPoint = `${environment.pubQueueApiUrl}/dashboard/v1/forms/acquiring/retrieve`;
        return this.http.post(endPoint, body).toPromise();
    }

    saveBusinessInfo(body): Promise<any> {
        const endPoint = `${environment.pubQueueApiUrl}/dashboard/v1/forms/${body.routeName}/acquiring`;

        return this.http.post(endPoint, body).toPromise();
    }

    updateBusinessInfo(body): Promise<any> {
        const endPoint = `${environment.pubQueueApiUrl}/dashboard/v1/forms/acquiring/update`;

        return this.http.post(endPoint, body).toPromise();
    }

    async getUserGroupsAttachedToUsers(routeName: string) {
        let franchiseId = await this.getFranchiseId(routeName);
        return await this.http
            .get<any>(`${environment.pubQueueApiUrlEU}/v1/usergroups/${franchiseId}/all/allUsers`).toPromise();
    }
}

class Flags { phoneFrame: boolean; minimumFeeOnDelivery: boolean; configureProductBonuses: boolean; globalOffers: boolean; weeklyMenu: boolean; externalIdMandatory: boolean; hasCatering?: boolean; hasCorporateCustomer?: boolean; hasOrderList?: boolean; hasStampCard?: boolean; hasSignage?: boolean; hasStaff?: boolean; hasWeeklyMenuOrdering?: boolean; }
var toggleFlags = new Flags()
export { toggleFlags }
